import React, {
  useCallback, useContext, useMemo, useState,
} from 'react'

import { string } from 'prop-types'

import useImageDownload from 'Pods/LazyLoaded/ImageGalleryPanel/Hooks/useImageDownload'
import useImageEdit from 'Pods/LazyLoaded/ImageGalleryPanel/Hooks/useImageEdit'
import useImageGenerate3d from 'Pods/LazyLoaded/ImageGalleryPanel/Hooks/useImageGenerate3d'
import GalleryFilters from 'Pods/Shared/GalleryFilters/GalleryFilters'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import Gallery from './Components/Gallery/Gallery'
import { DEFAULT_IMAGE_GALLERY_FILTERS } from './constants'
import { MOCK_IMAGE_CATEGORIES } from './Utils/mockImages'

// Community image gallery for /ai-image-generator/images. Filter state is local:
// unlike the 3D gallery nothing else in the studio reads it yet. `initialSearchQuery` seeds it
// for the per-object views (/ai-image-generator/gallery/jewelry), which open on that search and
// stay editable from there. Opening an image is handled inside the gallery (the expanded-view
// modal); editing, downloading and generating a 3D model from one here.
function ImageGalleryPanel({ initialSearchQuery = '' }) {
  const t = useTranslation()
  const handleImageDownload = useImageDownload()
  const handleImageEdit = useImageEdit()
  const handleImageGenerate3d = useImageGenerate3d()
  const { extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const categories = useMemo(
    () => MOCK_IMAGE_CATEGORIES.map(({ nameKey, value }) => ({ name: t(nameKey), value })),
    [t],
  )
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [category, setCategory] = useState(DEFAULT_IMAGE_GALLERY_FILTERS.category)

  // On an object view "no filters" means the object's own query, so clearing returns there
  // rather than to the unfiltered gallery under a heading that promises one object.
  const hasActiveFilters = searchQuery !== initialSearchQuery
    || category !== DEFAULT_IMAGE_GALLERY_FILTERS.category

  const handleClearFilters = useCallback(() => {
    setSearchQuery(initialSearchQuery)
    setCategory(DEFAULT_IMAGE_GALLERY_FILTERS.category)
  }, [initialSearchQuery])

  return (
    <div className="image-gallery-panel">
      <GalleryFilters
        className="image-gallery-panel__filters"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('imageGallery.searchPlaceholder')}
        category={category}
        categories={categories}
        onCategoryChange={setCategory}
        morePlaceholder={t('imageGallery.moreCategories')}
      />
      <Gallery
        searchQuery={searchQuery}
        category={category}
        extractingCoreStyles={extractingCoreStyles}
        onImageEdit={handleImageEdit}
        onImageDownload={handleImageDownload}
        onImageGenerate3d={handleImageGenerate3d}
        emptyState={(
          <EmptyState hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
        )}
      />
    </div>
  )
}

ImageGalleryPanel.propTypes = {
  initialSearchQuery: string,
}

export default ImageGalleryPanel
